import React, { useState, useEffect } from 'react';
import { DeskLayout } from '../../components/DeskLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Skeleton } from '../../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { getProperties, createProperty, updateProperty } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { 
  Plus, 
  Building2, 
  MapPin, 
  Mail, 
  Phone, 
  ExternalLink,
  Save,
  Mic,
  FileText,
  HelpCircle,
  Send,
  Gift,
  Users,
  Layout,
  Clock,
  QrCode,
  Copy
} from 'lucide-react';
import { toast } from 'sonner';
import { GuestHubQRDialog } from '../../components/GuestHubQRDialog';
import { OfficeHoursTab } from '../../components/OfficeHoursTab';

export const Properties = () => {
  const { isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [showNewProperty, setShowNewProperty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editedProperty, setEditedProperty] = useState(null);
  const [qrOpen, setQrOpen] = useState(false);
  const [newPropertyData, setNewPropertyData] = useState({
    name: '',
    slug: '',
    location: '',
    contactEmail: '',
    contactPhone: '',
    approvalMode: 'MANAGED',
    status: 'onboarding',
    main_contact_name: '',
    main_contact_email: '',
    notes: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (selectedProperty) {
      setEditedProperty({ ...selectedProperty });
    }
  }, [selectedProperty]);

  const loadData = async () => {
    try {
      const res = await getProperties();
      setProperties(res.data);
      if (res.data.length > 0) {
        setSelectedProperty(res.data[0]);
      }
    } catch (error) {
      console.error('Failed to load properties:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProperty = async () => {
    if (!newPropertyData.name || !newPropertyData.slug) {
      toast.error('Name and slug are required');
      return;
    }
    try {
      const res = await createProperty(newPropertyData);
      setShowNewProperty(false);
      setNewPropertyData({
        name: '', slug: '', location: '', contactEmail: '', contactPhone: '',
        approvalMode: 'MANAGED', status: 'onboarding',
        main_contact_name: '', main_contact_email: '', notes: ''
      });
      await loadData();
      setSelectedProperty(res.data);
      toast.success('Client workspace created');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create client workspace');
    }
  };

  const handleSaveProperty = async () => {
    if (!editedProperty) return;
    setSaving(true);
    try {
      await updateProperty(editedProperty.id, editedProperty);
      await loadData();
      toast.success('Workspace saved');
    } catch (error) {
      toast.error('Failed to save workspace');
    } finally {
      setSaving(false);
    }
  };

  const updateBrandVoice = (field, value) => {
    setEditedProperty({
      ...editedProperty,
      brandVoice: { ...editedProperty.brandVoice, [field]: value }
    });
  };

  const updatePolicies = (field, value) => {
    setEditedProperty({
      ...editedProperty,
      policies: { ...editedProperty.policies, [field]: value }
    });
  };

  if (loading) {
    return (
      <DeskLayout>
        <div className="space-y-6">
          <Skeleton className="h-8 w-48" />
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            <Skeleton className="h-96" />
            <Skeleton className="lg:col-span-3 h-96" />
          </div>
        </div>
      </DeskLayout>
    );
  }

  return (
    <DeskLayout>
      <div className="space-y-6 animate-fade-in" data-testid="properties-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading font-medium text-stone-900">Client Workspaces</h1>
            <p className="text-stone-500 mt-1">Set up and manage client/property workspaces.</p>
          </div>
          {isAdmin && (
            <Dialog open={showNewProperty} onOpenChange={setShowNewProperty}>
              <DialogTrigger asChild>
                <Button className="bg-[#3F617E] hover:bg-[#345470] rounded-full" data-testid="add-property-button">
                  <Plus className="h-4 w-4 mr-1" /> Add Client Workspace
                </Button>
              </DialogTrigger>
              <DialogContent
                className="sm:max-w-md p-0 max-h-[90vh] flex flex-col gap-0"
                data-testid="add-property-dialog"
              >
                <DialogHeader className="px-6 pt-6 pb-4 border-b border-stone-100">
                  <DialogTitle className="font-heading">Add Client Workspace</DialogTitle>
                </DialogHeader>
                <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
                  <div className="space-y-2">
                    <Label>Workspace Name *</Label>
                    <Input 
                      value={newPropertyData.name} 
                      onChange={(e) => setNewPropertyData({...newPropertyData, name: e.target.value})}
                      placeholder="SeaGlass Villa"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Slug * (for GuestHub URL)</Label>
                    <Input 
                      value={newPropertyData.slug} 
                      onChange={(e) => setNewPropertyData({...newPropertyData, slug: e.target.value.toLowerCase().replace(/\s+/g, '-')})}
                      placeholder="seaglass-villa"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Location</Label>
                    <Input 
                      value={newPropertyData.location} 
                      onChange={(e) => setNewPropertyData({...newPropertyData, location: e.target.value})}
                      placeholder="Santorini, Greece"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Email</Label>
                      <Input 
                        value={newPropertyData.contactEmail} 
                        onChange={(e) => setNewPropertyData({...newPropertyData, contactEmail: e.target.value})}
                        placeholder="hello@property.com"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Phone</Label>
                      <Input 
                        value={newPropertyData.contactPhone} 
                        onChange={(e) => setNewPropertyData({...newPropertyData, contactPhone: e.target.value})}
                        placeholder="+1 234 567 8900"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Status</Label>
                    <Select
                      value={newPropertyData.status}
                      onValueChange={(v) => setNewPropertyData({ ...newPropertyData, status: v })}
                    >
                      <SelectTrigger data-testid="new-property-status-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="onboarding">Onboarding</SelectItem>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="paused">Paused</SelectItem>
                        <SelectItem value="archived">Archived</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-[11px] text-stone-400">New workspaces start in onboarding while setup is in progress.</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Main contact name</Label>
                      <Input
                        value={newPropertyData.main_contact_name}
                        onChange={(e) => setNewPropertyData({ ...newPropertyData, main_contact_name: e.target.value })}
                        placeholder="Sofia Andreou"
                        data-testid="new-property-main-contact-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Main contact email</Label>
                      <Input
                        value={newPropertyData.main_contact_email}
                        onChange={(e) => setNewPropertyData({ ...newPropertyData, main_contact_email: e.target.value })}
                        placeholder="sofia@property.com"
                        data-testid="new-property-main-contact-email"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Internal notes (optional)</Label>
                    <Textarea
                      rows={3}
                      value={newPropertyData.notes}
                      onChange={(e) => setNewPropertyData({ ...newPropertyData, notes: e.target.value })}
                      placeholder="Onboarding context, account quirks, anything the team should know…"
                      data-testid="new-property-notes"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Approval Mode</Label>
                    <Select value={newPropertyData.approvalMode} onValueChange={(v) => setNewPropertyData({...newPropertyData, approvalMode: v})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="MANAGED">Managed (Direct Send)</SelectItem>
                        <SelectItem value="DRAFT_APPROVE">Draft + Approve</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {/* Sticky footer — always visible regardless of form length */}
                <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-stone-100 bg-white rounded-b-lg">
                  <Button
                    variant="ghost"
                    onClick={() => setShowNewProperty(false)}
                    className="rounded-full text-stone-600 hover:text-stone-900"
                    data-testid="cancel-new-property-button"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleCreateProperty}
                    className="bg-[#3F617E] hover:bg-[#345470] rounded-full"
                    data-testid="submit-new-property-button"
                  >
                    Create Client Workspace
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Main Content */}
        {properties.length === 0 ? (
          <Card className="border-stone-200" data-testid="properties-empty-state">
            <CardContent className="py-16 px-6">
              <div className="max-w-md mx-auto text-center space-y-4">
                <div className="h-14 w-14 rounded-2xl bg-[#3F617E]/10 text-[#3F617E] grid place-items-center mx-auto">
                  <Building2 className="h-7 w-7" />
                </div>
                <div className="space-y-1.5">
                  <h2 className="text-lg font-heading font-medium text-stone-900">
                    No client workspaces yet
                  </h2>
                  <p className="text-sm text-stone-500 leading-relaxed">
                    Add your first client workspace when you are ready to onboard a property.
                  </p>
                </div>
                {isAdmin && (
                  <div className="pt-2">
                    <Button
                      onClick={() => setShowNewProperty(true)}
                      className="bg-[#3F617E] hover:bg-[#345470] rounded-full"
                      data-testid="empty-state-add-property"
                    >
                      <Plus className="h-4 w-4 mr-1" /> Add your first client workspace
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        ) : (
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Property List */}
          <Card className="border-stone-200">
            <CardContent className="p-2">
              <div className="space-y-1">
                {properties.map((prop) => {
                  const isSelected = selectedProperty?.id === prop.id;
                  const statusKey = (prop.status || 'active').toLowerCase();
                  const STATUS_TONE = {
                    onboarding: isSelected ? 'bg-white/15 text-white border-white/20' : 'bg-amber-50 text-amber-700 border-amber-200',
                    active:     isSelected ? 'bg-white/15 text-white border-white/20' : 'bg-emerald-50 text-emerald-700 border-emerald-200',
                    paused:     isSelected ? 'bg-white/15 text-white border-white/20' : 'bg-stone-100 text-stone-600 border-stone-200',
                    archived:   isSelected ? 'bg-white/10 text-white/70 border-white/10' : 'bg-stone-50 text-stone-400 border-stone-200',
                  };
                  const tone = STATUS_TONE[statusKey] || STATUS_TONE.active;
                  return (
                  <button
                    key={prop.id}
                    onClick={() => setSelectedProperty(prop)}
                    className={`w-full text-left p-4 rounded-xl transition-colors ${
                      isSelected
                        ? 'bg-[#3F617E] text-white'
                        : 'hover:bg-stone-100'
                    }`}
                    data-testid={`property-item-${prop.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <Building2 className="h-5 w-5" />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <p className="font-medium truncate">{prop.name}</p>
                          <span
                            className={`inline-flex items-center text-[10px] tracking-[0.14em] uppercase px-1.5 py-0.5 rounded-full border ${tone}`}
                            data-testid={`property-status-badge-${prop.id}`}
                          >
                            {statusKey}
                          </span>
                        </div>
                        <p className={`text-sm ${isSelected ? 'text-white/70' : 'text-stone-500'} truncate`}>
                          {prop.location || 'No location'}
                        </p>
                        {prop.main_contact_name && (
                          <p className={`text-xs ${isSelected ? 'text-white/60' : 'text-stone-400'} truncate mt-0.5`}>
                            Contact: {prop.main_contact_name}
                          </p>
                        )}
                      </div>
                    </div>
                  </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Property Detail */}
          {selectedProperty && editedProperty && (
            <Card className="lg:col-span-3 border-stone-200">
              <CardHeader className="pb-4 flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="font-heading text-xl">{selectedProperty.name}</CardTitle>
                  <p className="text-sm text-stone-500 flex items-center gap-2 mt-1">
                    <MapPin className="h-4 w-4" />
                    {selectedProperty.location || 'No location set'}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {isAdmin && (
                    <Button 
                      onClick={handleSaveProperty}
                      disabled={saving}
                      className="bg-[#3F617E] hover:bg-[#345470] rounded-full"
                      data-testid="save-property-button"
                    >
                      <Save className="h-4 w-4 mr-1" />
                      {saving ? 'Saving…' : 'Save Changes'}
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {/* Guest Hub portal — prominent CTA card */}
                {selectedProperty.slug && (
                  <div className="mb-6 rounded-xl border-2 border-[#3F617E]/20 bg-gradient-to-br from-[#3F617E]/5 to-[#3F617E]/10 p-4 sm:p-5" data-testid="guesthub-portal-card">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <div className="h-8 w-8 rounded-full bg-[#3F617E] text-white grid place-items-center">
                            <QrCode className="h-4 w-4" />
                          </div>
                          <h4 className="text-sm font-semibold text-stone-900">Guest Hub portal — share this with guests</h4>
                        </div>
                        <p className="text-xs text-stone-600 mt-1.5">
                          Public, mobile-first request form. Submissions appear instantly in Guest Inbox, Today, and Daily Run.
                        </p>
                        <div className="mt-2.5 flex items-center gap-2 bg-white rounded-lg border border-stone-200 px-3 py-2">
                          <code className="text-xs text-stone-700 truncate flex-1" data-testid="guesthub-url-text">
                            {`${window.location.origin}/g/${selectedProperty.slug}`}
                          </code>
                        </div>
                        <p className="text-[11px] text-stone-500 mt-2 leading-relaxed">
                          Share this QR code with guests so they can submit requests directly to GuestHub.
                        </p>
                      </div>
                      <div className="flex sm:flex-col gap-2 flex-shrink-0">
                        <Button
                          variant="outline"
                          className="rounded-full"
                          onClick={() => {
                            navigator.clipboard?.writeText(`${window.location.origin}/g/${selectedProperty.slug}`);
                            toast.success('GuestHub link copied');
                          }}
                          data-testid="copy-guesthub-link"
                        >
                          <Copy className="h-3.5 w-3.5 mr-1" /> Copy link
                        </Button>
                        <Button
                          variant="outline"
                          className="rounded-full"
                          onClick={() => setQrOpen(true)}
                          data-testid="show-guesthub-qr"
                        >
                          <QrCode className="h-3.5 w-3.5 mr-1" /> GuestHub QR
                        </Button>
                        <a
                          href={`/g/${selectedProperty.slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid="open-guesthub-link"
                        >
                          <Button className="bg-[#3F617E] hover:bg-[#345470] rounded-full w-full">
                            <ExternalLink className="h-3.5 w-3.5 mr-1" /> Open
                          </Button>
                        </a>
                      </div>
                    </div>
                  </div>
                )}
                {selectedProperty.slug && (
                  <GuestHubQRDialog
                    open={qrOpen}
                    onOpenChange={setQrOpen}
                    propertyName={selectedProperty.name}
                    slug={selectedProperty.slug}
                    url={`${window.location.origin}/g/${selectedProperty.slug}`}
                  />
                )}
                <Tabs defaultValue="overview" className="w-full">
                  <TabsList className="grid w-full grid-cols-8 mb-6">
                    <TabsTrigger value="overview"><Building2 className="h-4 w-4 mr-1" /> Overview</TabsTrigger>
                    <TabsTrigger value="voice"><Mic className="h-4 w-4 mr-1" /> Voice</TabsTrigger>
                    <TabsTrigger value="policies"><FileText className="h-4 w-4 mr-1" /> Policies</TabsTrigger>
                    <TabsTrigger value="faqs"><HelpCircle className="h-4 w-4 mr-1" /> FAQs</TabsTrigger>
                    <TabsTrigger value="quick"><Send className="h-4 w-4 mr-1" /> Quick</TabsTrigger>
                    <TabsTrigger value="upsells"><Gift className="h-4 w-4 mr-1" /> Upsells</TabsTrigger>
                    <TabsTrigger value="hub"><Layout className="h-4 w-4 mr-1" /> Hub</TabsTrigger>
                    <TabsTrigger value="hours" data-testid="property-hours-tab-trigger"><Clock className="h-4 w-4 mr-1" /> Hours</TabsTrigger>
                  </TabsList>

                  <TabsContent value="overview" className="space-y-4" data-testid="property-overview-tab">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Workspace status</Label>
                        <Select
                          value={editedProperty.status || 'active'}
                          onValueChange={(v) => setEditedProperty({ ...editedProperty, status: v })}
                          disabled={!isAdmin}
                        >
                          <SelectTrigger data-testid="edit-property-status-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="onboarding">Onboarding</SelectItem>
                            <SelectItem value="active">Active</SelectItem>
                            <SelectItem value="paused">Paused</SelectItem>
                            <SelectItem value="archived">Archived</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-[11px] text-stone-400">
                          Onboarding while setup is in progress · Active once the client is live.
                        </p>
                      </div>
                      <div className="space-y-2">
                        <Label>Location</Label>
                        <Input
                          value={editedProperty.location || ''}
                          onChange={(e) => setEditedProperty({ ...editedProperty, location: e.target.value })}
                          placeholder="Santorini, Greece"
                          disabled={!isAdmin}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Main contact name</Label>
                        <Input
                          value={editedProperty.main_contact_name || ''}
                          onChange={(e) => setEditedProperty({ ...editedProperty, main_contact_name: e.target.value })}
                          placeholder="Primary contact at the property"
                          disabled={!isAdmin}
                          data-testid="edit-property-main-contact-name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Main contact email</Label>
                        <Input
                          value={editedProperty.main_contact_email || ''}
                          onChange={(e) => setEditedProperty({ ...editedProperty, main_contact_email: e.target.value })}
                          placeholder="contact@property.com"
                          disabled={!isAdmin}
                          data-testid="edit-property-main-contact-email"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Internal notes</Label>
                      <Textarea
                        rows={4}
                        value={editedProperty.notes || ''}
                        onChange={(e) => setEditedProperty({ ...editedProperty, notes: e.target.value })}
                        placeholder="Visible only to your team — context, quirks, onboarding notes…"
                        disabled={!isAdmin}
                        data-testid="edit-property-notes"
                      />
                    </div>
                  </TabsContent>

                  <TabsContent value="voice" className="space-y-6">
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label>Brand Voice Description</Label>
                        <Textarea
                          value={editedProperty.brandVoice?.description || ''}
                          onChange={(e) => updateBrandVoice('description', e.target.value)}
                          placeholder="Describe your property's voice and tone..."
                          rows={3}
                          disabled={!isAdmin}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Do Say (comma separated)</Label>
                          <Textarea
                            value={editedProperty.brandVoice?.doSay?.join(', ') || ''}
                            onChange={(e) => updateBrandVoice('doSay', e.target.value.split(',').map(s => s.trim()))}
                            placeholder="Welcome home, We're here for you..."
                            rows={3}
                            disabled={!isAdmin}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Don't Say (comma separated)</Label>
                          <Textarea
                            value={editedProperty.brandVoice?.dontSay?.join(', ') || ''}
                            onChange={(e) => updateBrandVoice('dontSay', e.target.value.split(',').map(s => s.trim()))}
                            placeholder="Policy states, Unfortunately..."
                            rows={3}
                            disabled={!isAdmin}
                          />
                        </div>
                      </div>
                      <div className="flex items-center justify-between p-4 bg-stone-50 rounded-lg">
                        <div>
                          <p className="font-medium">Approval Mode</p>
                          <p className="text-sm text-stone-500">
                            {editedProperty.approvalMode === 'MANAGED' ? 'Operators can send directly' : 'Drafts require approval'}
                          </p>
                        </div>
                        <Select 
                          value={editedProperty.approvalMode} 
                          onValueChange={(v) => setEditedProperty({...editedProperty, approvalMode: v})}
                          disabled={!isAdmin}
                        >
                          <SelectTrigger className="w-48">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="MANAGED">Managed</SelectItem>
                            <SelectItem value="DRAFT_APPROVE">Draft + Approve</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </TabsContent>

                  <TabsContent value="policies" className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Check-in Time</Label>
                        <Input
                          value={editedProperty.policies?.checkIn || ''}
                          onChange={(e) => updatePolicies('checkIn', e.target.value)}
                          placeholder="3:00 PM"
                          disabled={!isAdmin}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Check-out Time</Label>
                        <Input
                          value={editedProperty.policies?.checkOut || ''}
                          onChange={(e) => updatePolicies('checkOut', e.target.value)}
                          placeholder="11:00 AM"
                          disabled={!isAdmin}
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Cancellation Policy</Label>
                      <Textarea
                        value={editedProperty.policies?.cancellation || ''}
                        onChange={(e) => updatePolicies('cancellation', e.target.value)}
                        placeholder="Free cancellation up to 7 days before arrival"
                        rows={2}
                        disabled={!isAdmin}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Deposit Policy</Label>
                      <Input
                        value={editedProperty.policies?.deposit || ''}
                        onChange={(e) => updatePolicies('deposit', e.target.value)}
                        placeholder="30% deposit required"
                        disabled={!isAdmin}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Late Checkout</Label>
                      <Input
                        value={editedProperty.policies?.lateCheckout || ''}
                        onChange={(e) => updatePolicies('lateCheckout', e.target.value)}
                        placeholder="Available upon request, €50/hour"
                        disabled={!isAdmin}
                      />
                    </div>
                  </TabsContent>

                  <TabsContent value="faqs" className="space-y-4">
                    <div className="space-y-3">
                      {editedProperty.faqItems?.map((faq, idx) => (
                        <div key={idx} className="p-4 border border-stone-200 rounded-lg">
                          <p className="font-medium text-stone-900">{faq.q}</p>
                          <p className="text-sm text-stone-600 mt-1">{faq.a}</p>
                        </div>
                      ))}
                      {(!editedProperty.faqItems || editedProperty.faqItems.length === 0) && (
                        <p className="text-center text-stone-500 py-8">No FAQs configured</p>
                      )}
                    </div>
                  </TabsContent>

                  <TabsContent value="quick" className="space-y-4">
                    <div className="flex flex-wrap gap-2">
                      {editedProperty.quickReplies?.map((reply, idx) => (
                        <Badge key={idx} variant="secondary" className="text-sm py-2 px-4">
                          {reply}
                        </Badge>
                      ))}
                      {(!editedProperty.quickReplies || editedProperty.quickReplies.length === 0) && (
                        <p className="text-center text-stone-500 py-8 w-full">No quick replies configured</p>
                      )}
                    </div>
                  </TabsContent>

                  <TabsContent value="upsells" className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      {editedProperty.upsells?.map((upsell, idx) => (
                        <div key={idx} className="p-4 border border-stone-200 rounded-lg">
                          <div className="flex items-center justify-between">
                            <p className="font-medium text-stone-900">{upsell.name}</p>
                            <Badge variant="secondary">{upsell.price}</Badge>
                          </div>
                          <p className="text-sm text-stone-600 mt-1">{upsell.description}</p>
                        </div>
                      ))}
                      {(!editedProperty.upsells || editedProperty.upsells.length === 0) && (
                        <p className="text-center text-stone-500 py-8 col-span-2">No upsells configured</p>
                      )}
                    </div>
                  </TabsContent>

                  <TabsContent value="hub" className="space-y-4">
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label>Welcome Message</Label>
                        <Textarea
                          value={editedProperty.guestHubContent?.welcome || ''}
                          onChange={(e) => setEditedProperty({
                            ...editedProperty,
                            guestHubContent: { ...editedProperty.guestHubContent, welcome: e.target.value }
                          })}
                          placeholder="Welcome to our property..."
                          rows={3}
                          disabled={!isAdmin}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Check-in Instructions</Label>
                        <Textarea
                          value={editedProperty.guestHubContent?.checkin || ''}
                          onChange={(e) => setEditedProperty({
                            ...editedProperty,
                            guestHubContent: { ...editedProperty.guestHubContent, checkin: e.target.value }
                          })}
                          placeholder="Check-in details..."
                          rows={3}
                          disabled={!isAdmin}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>House Guide</Label>
                        <Textarea
                          value={editedProperty.guestHubContent?.guide || ''}
                          onChange={(e) => setEditedProperty({
                            ...editedProperty,
                            guestHubContent: { ...editedProperty.guestHubContent, guide: e.target.value }
                          })}
                          placeholder="House guide content..."
                          rows={3}
                          disabled={!isAdmin}
                        />
                      </div>
                    </div>
                  </TabsContent>

                  <TabsContent value="hours" className="space-y-4" data-testid="property-hours-tab">
                    <OfficeHoursTab property={editedProperty} isAdmin={isAdmin} />
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          )}
        </div>
        )}
      </div>
    </DeskLayout>
  );
};

export default Properties;
